import { ComponentType, lazy, LazyExoticComponent } from 'react'

import type { Scope } from '@sentry/react'

import wait from 'core/helpers/wait'
import ErrorLogging from 'core/system/ErrorLogging'

type ModuleImport<T extends ComponentType<any>> = () => Promise<{ default: T }>

// Chunk URL inside a failed dynamic import message, e.g. Chrome's
// "Failed to fetch dynamically imported module: https://host/assets/Foo-hash.js".
// Safari reports "Importing a module script failed." with no URL, so callers
// must handle a missing match.
const CHUNK_URL_PATTERN = /(https?:\/\/[^\s'"]+\.js)/

const RETRY_DELAY_MS = 500

const chunkUrlFromError = (error: unknown): string | undefined =>
  error instanceof Error ? CHUNK_URL_PATTERN.exec(error.message)?.[1] : undefined

/**
 * Loads a lazy chunk, and recovers from the two ways the load fails in prod.
 *
 * A plain retry covers a transient network failure. It does not cover a bad
 * response held in the HTTP cache: prod serves assets with `max-age=14400`, so
 * a poisoned entry answers every later request for that URL — including the
 * ones a page reload makes. Only a different URL escapes it, so the last
 * attempt adds a cache-busting query. Cloudflare Pages ignores the query when
 * it matches a static asset, and returns the same file (sc-77847).
 */
const importWithRetry = async <T extends ComponentType<any>>(
  importModule: ModuleImport<T>,
): Promise<{ default: T }> => {
  try {
    return await importModule()
  } catch {
    await wait(RETRY_DELAY_MS)
  }

  try {
    return await importModule()
  } catch (error) {
    const chunkUrl = chunkUrlFromError(error)
    // Without a URL there is nothing left to try. The error boundary shows the
    // report screen, which offers a manual reload.
    if (!chunkUrl) throw error

    ErrorLogging.withScope((scope: Scope) => {
      scope.setTag('ident', 'ChunkLoadRetry')
      scope.setTag('chunkUrl', chunkUrl)
      ErrorLogging.captureMessage('Retrying a failed chunk load past the HTTP cache', 'warning')
    })

    return import(/* @vite-ignore */ `${chunkUrl}?cacheBust=${Date.now()}`) as Promise<{ default: T }>
  }
}

/**
 * Drop-in replacement for `React.lazy` that retries a failed chunk load.
 */
const lazyWithRetry = <T extends ComponentType<any>>(importModule: ModuleImport<T>): LazyExoticComponent<T> =>
  lazy(() => importWithRetry(importModule))

export { chunkUrlFromError, importWithRetry }

export default lazyWithRetry
